import {
  autocompleteClasses,
  Box,
  BoxProps,
  buttonBaseClasses,
  inputBaseClasses,
  styled,
} from '@chronosphereio/chrono-ui';
import { ReactNode } from 'react';

export interface CombinedControlsProps {
  children: ReactNode;
  flex?: BoxProps['flex'];
  justifyContent?: BoxProps['justifyContent'];
}

/**
 * Internal-only component that helps with the complex CSS and layout associated
 * with combining a variety of controls into a single item visually.
 *
 * This component is currently configured to work for buttons (using `CombinedControlsButton` or
 * `CombinedControlsIconButton`) and selects (using `Select` from `chrono-ui`) as immediate or
 * nested children. It may need to be tweaked to cover additional cases in the future.
 */
export function CombinedControls({ children, flex, justifyContent }: CombinedControlsProps) {
  return (
    <CombinedControlsWrapper flex={flex} justifyContent={justifyContent}>
      {children}
    </CombinedControlsWrapper>
  );
}

const CombinedControlsWrapper = styled(Box)(({ theme }) => {
  const inputBaseSelector = `&.${inputBaseClasses.root}, .${inputBaseClasses.root}`;
  const autocompleteBaseSelector = `&.${autocompleteClasses.root}, .${autocompleteClasses.root}`;
  const buttonBaseSelector = `&.${buttonBaseClasses.root}, .${buttonBaseClasses.root}`;
  const layoutControlsSelector = [autocompleteBaseSelector, buttonBaseSelector].join(', ');
  const borderControlsSelector = [inputBaseSelector, buttonBaseSelector].join(', ');

  return {
    borderRadius: theme.featureFlags.panwRebrandStubYear ? theme.shape.borderRadiusSm : theme.shape.borderRadius,
    display: 'flex',

    // Middle items and common settings.
    // There isn't a great selector for "middle and not only", so we set the "middle"
    // css on everything and then override below in more specific selectors.
    '> *': {
      [layoutControlsSelector]: {
        // Shift slightly to the left to collapse borders.
        marginLeft: '-1px',
      },
      [borderControlsSelector]: {
        // No border radius for middle items.
        borderRadius: 0,

        [`&:hover, &:focus, &.${inputBaseClasses.focused}, &.${buttonBaseClasses.focusVisible}`]: {
          // Ensure that the right borders show up on hover/focus. Needed to
          // account for the potential overlaps from the negative margins used
          // to collapse the borders.
          zIndex: 1,
        },
      },
    },

    // First item
    '> *:first-child': {
      [layoutControlsSelector]: {
        // No border collapsing needed.
        marginLeft: 0,
      },
      [borderControlsSelector]: {
        // Only left border radius for the first item.
        borderTopLeftRadius: theme.featureFlags.panwRebrandStubYear
          ? theme.shape.borderRadiusSm
          : theme.shape.borderRadius,
        borderBottomLeftRadius: theme.featureFlags.panwRebrandStubYear
          ? theme.shape.borderRadiusSm
          : theme.shape.borderRadius,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
    },

    // Last item
    '> *:last-child': {
      [borderControlsSelector]: {
        // Only right border radius for the last item.
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderTopRightRadius: theme.featureFlags.panwRebrandStubYear
          ? theme.shape.borderRadiusSm
          : theme.shape.borderRadius,
        borderBottomRightRadius: theme.featureFlags.panwRebrandStubYear
          ? theme.shape.borderRadiusSm
          : theme.shape.borderRadius,
      },
    },

    // Only item.
    '> *:first-child:last-child': {
      [layoutControlsSelector]: {
        // No border collapsing needed.
        marginLeft: 0,
      },
      [borderControlsSelector]: {
        // All border radius when single item.
        borderRadius: theme.featureFlags.panwRebrandStubYear ? theme.shape.borderRadiusSm : theme.shape.borderRadius,
      },
    },
  };
});
